import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useTerritoryStore } from '../../../store/territoryStore';
import { useUserStore } from '../../../store/userStore';
import api from '../../../lib/api';

interface OrgOption {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
}

interface NamedAccountFormProps {
  onClose: () => void;
}

export default function NamedAccountForm({ onClose }: NamedAccountFormProps) {
  const { createNamedAccount } = useTerritoryStore();
  const { users, fetchUsers } = useUserStore();

  const [orgSearch, setOrgSearch] = useState('');
  const [orgResults, setOrgResults] = useState<OrgOption[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<OrgOption | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [assignedUserId, setAssignedUserId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Debounced org search
  useEffect(() => {
    if (!orgSearch.trim() || selectedOrg) {
      setOrgResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await api.get('/api/organizations', {
          params: { search: orgSearch.trim(), limit: 10 },
        });
        setOrgResults(res.data.organizations ?? []);
      } catch {
        setOrgResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [orgSearch, selectedOrg]);

  const handleSelectOrg = (org: OrgOption) => {
    setSelectedOrg(org);
    setOrgSearch(org.name);
    setOrgResults([]);
  };

  const handleClearOrg = () => {
    setSelectedOrg(null);
    setOrgSearch('');
    setOrgResults([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedOrg) return setError('Select an organization.');
    if (!assignedUserId) return setError('Select a rep to assign.');

    setIsSaving(true);
    try {
      await createNamedAccount({ organizationId: selectedOrg.id, assignedUserId });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Failed to create named account.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Modal header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Add Named Account</h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
              {error}
            </p>
          )}

          {/* Organization search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Organization <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={orgSearch}
                onChange={(e) => {
                  if (selectedOrg) handleClearOrg();
                  setOrgSearch(e.target.value);
                }}
                placeholder="Search organizations..."
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {selectedOrg && (
                <button
                  type="button"
                  onClick={handleClearOrg}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={14} />
                </button>
              )}

              {/* Dropdown results */}
              {orgResults.length > 0 && (
                <ul className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {orgResults.map((org) => (
                    <li key={org.id}>
                      <button
                        type="button"
                        onClick={() => handleSelectOrg(org)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      >
                        <span className="font-medium text-gray-900 dark:text-gray-100">{org.name}</span>
                        {(org.city || org.state) && (
                          <span className="ml-1.5 text-gray-400 dark:text-gray-500 text-xs">
                            {[org.city, org.state].filter(Boolean).join(', ')}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {isSearching && (
                <p className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-sm px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  Searching...
                </p>
              )}
            </div>
          </div>

          {/* Rep dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Assign To <span className="text-red-500">*</span>
            </label>
            <select
              value={assignedUserId}
              onChange={(e) => setAssignedUserId(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select rep...</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.displayName ?? u.email}
                </option>
              ))}
            </select>
          </div>

          {/* Footer buttons */}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="border border-gray-300 dark:border-gray-600 px-4 py-2 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-60"
            >
              {isSaving ? 'Saving...' : 'Add Named Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
